// Karta gramatyczna „Zwróć uwagę” (SPEC §22.2): jedna krótka notatka po polsku na pozycję szkieletu gramatycznego (grammar.ts)
// plus dwa przykłady z samej lekcji, w których wzorzec jest pogrubiony. Czysty kod — dane w src/content/grammarNotes.json.

import { grammarById } from './grammar.ts';
import type { LessonV2 } from './levels.ts';
import { splitSentences } from './lessonSteps.ts';

export interface GrammarNote {
  /** Tytuł karty po polsku (z angielskim przykładem). */
  title: string;
  /** JEDNA zasada, 1–2 zdania po polsku. */
  rule: string;
  /** Wzór w skrócie, np. „he / she / it + eats”. */
  formula: string;
  /** Typowy błąd Polaka i poprawna wersja. */
  mistake: { wrong: string; right: string };
  /** Wskazówka (różnica względem polskiego, wyjątek). */
  tip: string;
  /** Szybkie sprawdzenie: dwie odpowiedzi, jedna dobra (nie liczy się do wyniku). */
  check: { q: string; options: [string, string]; answer: string };
}

export type GrammarNotes = Record<string, GrammarNote>;

/** Kawałek zdania do wyświetlenia: pogrubiony (`focus`) albo zwykły. */
export interface TextPart {
  text: string;
  focus: boolean;
}

const escapeRe = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Dzieli zdanie na kawałki, pogrubiając podane słowa/frazy (bez rozróżniania wielkości liter, całe słowa; każde wystąpienie). */
export function highlightParts(text: string, focus: readonly string[]): TextPart[] {
  const words = focus.map((f) => f.trim()).filter(Boolean).sort((a, b) => b.length - a.length);
  if (words.length === 0) return [{ text, focus: false }];
  const re = new RegExp(`(?<![A-Za-z'’])(${words.map((w) => escapeRe(w).replace(/\s+/g, '\\s+')).join('|')})(?![A-Za-z'’])`, 'gi');
  const parts: TextPart[] = [];
  let last = 0;
  for (const m of text.matchAll(re)) {
    if (m.index > last) parts.push({ text: text.slice(last, m.index), focus: false });
    parts.push({ text: m[0], focus: true });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ text: text.slice(last), focus: false });
  return parts;
}

export interface GrammarCardExample {
  text: string;
  pl: string;
  focus: string[];
  parts: TextPart[];
}

export interface GrammarCard {
  id: string;
  label: string;
  labelPl: string;
  note: GrammarNote;
  examples: GrammarCardExample[];
}

const norm = (s: string): string => s.toLowerCase().replace(/[’‘]/g, "'").replace(/\s+/g, ' ').trim();

/** Tłumaczenie zdania z lekcji: pierwsza linia (krok 1, 2 lub scenka), w której to zdanie występuje; przy kwestiach wielozdaniowych — sparowane zdanie. */
export function translationOf(lesson: LessonV2, sentence: string): string | null {
  const want = norm(sentence);
  const lines = [...lesson.describe.lines, ...lesson.enrich.lines, ...lesson.scene.dialogue];
  for (const l of lines) {
    const pairs = splitSentences(l.text, l.pl);
    const hit = pairs.find((p) => norm(p.text) === want);
    if (hit) return hit.pl;
    if (pairs.length === 0 && norm(l.text).includes(want)) return l.pl;
  }
  return null;
}

/**
 * Czy to pierwsze spotkanie ze wzorcem lekcji (SPEC §23.4): karta „Zwróć uwagę” pokazuje się tylko wtedy — gdy żadna wcześniejsza lekcja kursu z tym samym wzorcem
 * nie jest jeszcze zrobiona. Powtarzalna: ponowne zrobienie pierwszej lekcji znów pokaże kartę, a kolejne lekcje z tym wzorcem już nie.
 */
export function grammarIsNew(lesson: Pick<LessonV2, 'id' | 'grammar'>, course: ReadonlyArray<Pick<LessonV2, 'id' | 'grammar'>>, done: Readonly<Record<string, unknown>>): boolean {
  const at = course.findIndex((l) => l.id === lesson.id);
  const earlier = at < 0 ? [] : course.slice(0, at);
  return !earlier.some((l) => l.grammar.id === lesson.grammar.id && done[l.id] !== undefined);
}

/** Karta gramatyczna lekcji: notatka pozycji + przykłady z lekcji (z tłumaczeniem i pogrubionym wzorcem); null, gdy brak notatki albo przykładów. */
export function grammarCard(lesson: LessonV2, notes: GrammarNotes): GrammarCard | null {
  const note = notes[lesson.grammar.id];
  const item = grammarById(lesson.grammar.id);
  const spot = lesson.grammar.spot?.examples ?? [];
  if (!note || !item || spot.length === 0) return null;
  const examples = spot.map((e): GrammarCardExample => ({ text: e.text, pl: translationOf(lesson, e.text) ?? '', focus: e.focus, parts: highlightParts(e.text, e.focus) }));
  return { id: item.id, label: item.label, labelPl: item.labelPl, note, examples };
}
